import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ApiError, listEvents, toApiError } from '../api/client';
import { useNow, usePolling } from '../api/hooks';
import { DeliveryRollupCell } from '../components/DeliveryRollup';
import { DemoPanel } from '../components/demo/DemoPanel';
import { EmptyState, ErrorBanner, ErrorState, LoadingState } from '../components/StateViews';
import { DeliveryStatusBadge } from '../components/StatusBadge';
import { rollUpDeliveries } from '../lib/deliveries';
import { formatAbsolute, formatDeliveryStatus, formatRelativeTime } from '../lib/format';
import { DELIVERY_STATUSES, type EventSummary } from '../types';

const POLL_INTERVAL_MS = 2000;
const LIMIT_OPTIONS = [25, 50, 100];

function sinceLabel(lastUpdatedAt: number | null, now: number): string {
  if (lastUpdatedAt === null) return 'never';
  const seconds = Math.max(0, Math.round((now - lastUpdatedAt) / 1000));
  return seconds < 2 ? 'just now' : `${seconds}s ago`;
}

export function EventFeedView() {
  const [paused, setPaused] = useState(false);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');

  // Older pages are accumulated separately; the live poll only ever owns page one.
  const [older, setOlder] = useState<EventSummary[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState<ApiError | null>(null);

  const viewingHistory = older.length > 0;
  const pollEnabled = !paused && !viewingHistory;

  const fetcher = useCallback(
    (signal: AbortSignal) => listEvents({ limit, status: status || null }, signal),
    [limit, status],
  );

  const { data, error, loading, lastUpdatedAt, refresh } = usePolling(
    fetcher,
    POLL_INTERVAL_MS,
    pollEnabled,
  );

  const now = useNow(1000);

  // A changed query invalidates any history already pulled in under the old one.
  useEffect(() => {
    setOlder([]);
    setOlderCursor(null);
    setOlderError(null);
  }, [limit, status]);

  const events = useMemo(() => {
    const merged = new Map<string, EventSummary>();
    for (const event of data?.data ?? []) merged.set(event.id, event);
    for (const event of older) if (!merged.has(event.id)) merged.set(event.id, event);
    return [...merged.values()];
  }, [data, older]);

  const nextCursor = viewingHistory ? olderCursor : (data?.nextCursor ?? null);

  const loadOlder = useCallback(async (): Promise<void> => {
    if (nextCursor === null) return;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      const page = await listEvents({ limit, status: status || null, cursor: nextCursor });
      setOlder((previous) => [...previous, ...page.data]);
      setOlderCursor(page.nextCursor);
    } catch (requestError) {
      setOlderError(toApiError(requestError));
    } finally {
      setLoadingOlder(false);
    }
  }, [limit, nextCursor, status]);

  const backToLive = useCallback(() => {
    setOlder([]);
    setOlderCursor(null);
    setOlderError(null);
    refresh();
  }, [refresh]);

  return (
    <div className="view">
      <header className="view__head">
        <div>
          <p className="view__kicker">Live feed</p>
          <h1 className="view__title">Events</h1>
          <p className="view__lede">
            Every ingested event and how far its deliveries have got. Updates every two seconds.
          </p>
        </div>
      </header>

      <DemoPanel onEventSent={refresh} />

      <section className="panel">
        <div className="toolbar">
          <div className="toolbar__group">
            <button
              type="button"
              className={`btn btn--sm ${paused ? 'btn--primary' : ''}`}
              onClick={() => setPaused((value) => !value)}
              aria-pressed={paused}
              disabled={viewingHistory}
              title={viewingHistory ? 'Polling is paused while older pages are loaded' : undefined}
            >
              {paused ? '▶ Resume' : '❚❚ Pause'}
            </button>

            <span className={`live ${pollEnabled ? 'live--on' : 'live--off'}`}>
              <span className="live__dot" aria-hidden="true" />
              {pollEnabled ? 'Live' : 'Paused'}
            </span>

            <span className="toolbar__meta" aria-live="polite">
              Updated {sinceLabel(lastUpdatedAt, now)}
            </span>
          </div>

          <div className="toolbar__group">
            <label className="toolbar__field">
              <span>Status</span>
              <select
                className="select"
                value={status}
                onChange={(changeEvent) => setStatus(changeEvent.target.value)}
              >
                <option value="">All</option>
                {DELIVERY_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {formatDeliveryStatus(value)}
                  </option>
                ))}
              </select>
            </label>

            <label className="toolbar__field">
              <span>Show</span>
              <select
                className="select"
                value={limit}
                onChange={(changeEvent) => setLimit(Number(changeEvent.target.value))}
              >
                {LIMIT_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <button type="button" className="btn btn--ghost btn--sm" onClick={refresh}>
              Refresh
            </button>
          </div>
        </div>

        {viewingHistory ? (
          <div className="notice notice--muted notice--inline">
            <span>Showing loaded history — live updates are paused.</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={backToLive}>
              Back to live
            </button>
          </div>
        ) : null}

        {error !== null && data !== null ? <ErrorBanner error={error} onRetry={refresh} /> : null}

        {error !== null && data === null ? (
          <ErrorState error={error} onRetry={refresh} context="the event feed" />
        ) : loading && data === null ? (
          <LoadingState label="Loading events" rows={6} />
        ) : events.length === 0 ? (
          <EmptyState title={status === '' ? 'No events yet' : 'No events match this filter'}>
            {status === '' ? (
              <>
                Nothing has been ingested. Open <strong>Send a test event</strong> above, or POST to{' '}
                <code className="mono">/events</code> from a terminal.
              </>
            ) : (
              <>
                No event has a delivery in the{' '}
                <strong>{formatDeliveryStatus(status)}</strong> state right now.
              </>
            )}
          </EmptyState>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Event</th>
                    <th scope="col" className="col--id">
                      ID
                    </th>
                    <th scope="col">Deliveries</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="col--right">
                      Ingested
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => {
                    const rollup = rollUpDeliveries(event.deliveries);
                    return (
                      <tr key={event.id}>
                        <td>
                          <Link className="table__link" to={`/events/${encodeURIComponent(event.id)}`}>
                            {event.eventType}
                          </Link>
                          {event.benchmarkRunId ? (
                            <span className="tag tag--accent" title="Part of a benchmark run">
                              {event.benchmarkRunId}
                            </span>
                          ) : null}
                        </td>
                        <td className="col--id">
                          <span className="mono muted">{event.id}</span>
                        </td>
                        <td>
                          <DeliveryRollupCell deliveries={event.deliveries} />
                        </td>
                        <td>
                          {rollup.overall === 'empty' ? (
                            <span className="muted">—</span>
                          ) : (
                            <DeliveryStatusBadge status={rollup.overall} size="sm" />
                          )}
                        </td>
                        <td className="col--right">
                          <time
                            className="muted"
                            dateTime={event.createdAt}
                            title={formatAbsolute(event.createdAt)}
                          >
                            {formatRelativeTime(event.createdAt, now)}
                          </time>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {olderError ? <ErrorBanner error={olderError} /> : null}

            <div className="table-foot">
              <span className="muted">
                {events.length} event{events.length === 1 ? '' : 's'}
              </span>
              {nextCursor !== null ? (
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => void loadOlder()}
                  disabled={loadingOlder}
                >
                  {loadingOlder ? 'Loading…' : 'Load older'}
                </button>
              ) : (
                <span className="muted">End of history</span>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
